import './addressSuggestion.scss';

import { useCallback, useEffect, useRef } from 'react';

import { formatSuggestion } from '../../store/addressSuggestionStore/functions/formatSuggestion';
import { getSuggestionKey } from '../../store/addressSuggestionStore/functions/getSuggestionKey';
import useAddressSuggestionStore from '../../store/addressSuggestionStore/useAddressSuggestionStore';
import { AddressSuggestionProps } from './types/AddressSuggestionProps';

export const AddressSuggestion = ({
  fieldName,
  onSuggestionSelect,
}: AddressSuggestionProps): JSX.Element | null => {
  const {
    activeFieldName,
    computed: { hasAddressSuggestions },
    addressSuggestions,
    selectedIndex,
    setSelectedIndex,
    clearSuggestions,
  } = useAddressSuggestionStore();

  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent): void => {
      const target = event.target as Node;

      if (!containerRef.current || containerRef.current.contains(target)) {
        return;
      }

      clearSuggestions();
    },
    [clearSuggestions],
  );

  useEffect(() => {
    // Handle click outside to close suggestions
    if (hasAddressSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);

      return (): void => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [hasAddressSuggestions, handleClickOutside]);

  if (!hasAddressSuggestions || activeFieldName !== fieldName) {
    return null;
  }

  return (
    <div className="address-suggestions" ref={containerRef}>
      {addressSuggestions.map((suggestion, index) => (
        <div
          className={`suggestion-item ${
            index === selectedIndex ? 'selected' : ''
          }`}
          key={getSuggestionKey(suggestion)}
          onClick={() => {
            onSuggestionSelect(index);
          }}
          onMouseEnter={() => {
            setSelectedIndex(index);
          }}
        >
          {formatSuggestion(suggestion)}
        </div>
      ))}
    </div>
  );
};
